import React, { useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/app/hook';
import { useGetAssistantsQuery } from '@/app/services/chatbot-rtk.services';
import { store } from '@/app/store';
import { BannerHelper } from '@/components/common/BannerToast';
import HintBlock from '@/components/common/HintBlock';
import { PSButton } from '@/components/ui/button';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import ApiLoading from '../../components/ui/ApiLoading';
import i18n from '../../i18n/config';
import { assistantSelectors, handleSetBasicAdditionalInfos, handleSetExtraAdditionalInfos, wizardState } from './botSlice';
import { AIKittyType } from './types';

type KeyIndexed =
  | 'company_name'
  | 'agent_name'
  | 'domain'
  | 'personality'
  | 'legal_sector'
  | 'business_sector'
  | 'disclaimer'
  | 'products_services'
  | 'brand'
  | 'target_audience'
  | 'subject_expertise'
  | 'education_level'
  | 'tech_specialization'
  | 'industry_focus'
  | 'market_area'
  | 'establishment_name'
  | 'cuisine_type'
  | 'event_specialization';

type Props = {
  formBuilder: UseFormReturn<any, any>;
};
const AiAdditionalInfoEdit = ({ formBuilder }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const wizard = useAppSelector(wizardState);

  const { isLoading, isError, isSuccess, refetch } = useGetAssistantsQuery({
    language: i18n.language,
  });

  const targetAssistantData = assistantSelectors.selectById(store.getState(), (wizard.bot_config as AIKittyType).assistant);

  const labelMapper: Record<string, string> = {
    company_name: t('Wizard/validationLabels/key8'),
    agent_name: t(wizard.category == 'clone' ? 'Wizard/validationLabels/key14' : 'Wizard/validationLabels/key9'),
    domain: t('Wizard/validationLabels/key10'),
    personality: t('Wizard/validationLabels/key11'),
    legal_sector: t('Wizard/validationLabels/key12'),
    business_sector: t('Wizard/validationLabels/key13'),
    disclaimer: t('Wizard/validationLabels/key31'),
    products_services: t('Wizard/validationLabels/key32'),
    brand: t('Wizard/validationLabels/key33'),
    target_audience: t('Wizard/validationLabels/key34'),
    subject_expertise: t('Wizard/validationLabels/key35'),
    education_level: t('Wizard/validationLabels/key36'),
    tech_specialization: t('Wizard/validationLabels/key37'),
    specialization: t('Wizard/validationLabels/key38'),
    industry_focus: t('Wizard/validationLabels/key39'),
    market_area: t('Wizard/validationLabels/key40'),
    establishment_name: t('Wizard/validationLabels/key41'),
    cuisine_type: t('Wizard/validationLabels/key42'),
    event_specialization: t('Wizard/validationLabels/key43'),
    agency_name: t('Wizard/validationLabels/key44'),
  };

  const capitalizeAndRemoveUnderscore = (str: string) => {
    try {
      return str
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    } catch (error) {
      return str;
    }
  };

  const updateBasicInfos = () => {
    dispatch(
      handleSetBasicAdditionalInfos({
        additional_instructions: formBuilder.watch('additional_instructions'),
        welcoming: formBuilder.watch('welcoming'),
      })
    );
  };

  const updateExtraInfos = () => {
    dispatch(
      handleSetExtraAdditionalInfos({
        agent_name: formBuilder.watch('agent_name'),
        company_name: formBuilder.watch('company_name'),
        domain: formBuilder.watch('domain'),
        personality: formBuilder.watch('personality'),
        domains: formBuilder.watch('domains'),
        legal_sector: formBuilder.watch('legal_sector'),
        business_sector: formBuilder.watch('business_sector'),
        disclaimer: formBuilder.watch('disclaimer'),
        products_services: formBuilder.watch('products_services'),
        brand: formBuilder.watch('brand'),
        target_audience: formBuilder.watch('target_audience'),
        subject_expertise: formBuilder.watch('subject_expertise'),
        education_level: formBuilder.watch('education_level'),
        tech_specialization: formBuilder.watch('tech_specialization'),
        specialization: formBuilder.watch('specialization'),
        industry_focus: formBuilder.watch('industry_focus'),
        market_area: formBuilder.watch('market_area'),
        establishment_name: formBuilder.watch('establishment_name'),
        cuisine_type: formBuilder.watch('cuisine_type'),
        event_specialization: formBuilder.watch('event_specialization'),
        agency_name: formBuilder.watch('agency_name'),
      })
    );
  };

  useEffect(() => {
    const instructions = (wizard.bot_config as AIKittyType)?.instructions;
    if (!instructions) return;

    const fieldsToSync = [
      'agent_name',
      'company_name',
      'domain',
      'personality',
      'legal_sector',
      'business_sector',
      'disclaimer',
      'products_services',
      'brand',
      'target_audience',
      'subject_expertise',
      'education_level',
      'tech_specialization',
      'specialization',
      'industry_focus',
      'market_area',
      'establishment_name',
      'cuisine_type',
      'event_specialization',
      'agency_name',
    ] as const;

    fieldsToSync.forEach(field => {
      if (instructions[field] != null) {
        formBuilder.setValue(field, instructions[field]);
      }
    });
  }, [wizard.bot_config, formBuilder]);

  const getErrorMessage = (errors: any, name: string) => {
    if (errors[name]?.type == 'max') {
      return t('Wizard/validationLabels/key16');
    }

    if (wizard.category === 'clone' && name === 'agent_name') {
      return t('Wizard/validationLabels/key15', {
        variable: t('Wizard/validationLabels/key14'),
      });
    }
    return t('Wizard/validationLabels/key15', {
      variable: labelMapper[name],
    });
  };

  let content: React.ReactNode;

  if (isLoading) {
    content = (
      <div className="tw-px-8 tw-w-full tw-justify-center tw-flex-col tw-items-center tw-flex tw-min-h-[50vh]">
        <ApiLoading />
      </div>
    );
  }

  if (isError) {
    content = (
      <div>
        {t('Wizard/general/key10')}
        <PSButton id="btn-addiotional-refetch" variant="link" onClick={refetch} title={t('Wizard/general/key11')} />
      </div>
    );
  }

  if (isSuccess) {
    content = (
      <div className="tw-flex tw-flex-col tw-gap-3 tw-items-start">
        <div className="tw-text-gray-800 tw-font-semibold tw-font-['Lexend'] tw-leading-7 tw-text-2xl">{t('Wizard/AiAdditionalInfo/key7')}</div>
        <div className="tw-w-full tw-mb-3 tw-border-solid tw-p-4 tw-bg-white tw-rounded-2xl tw-shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06)] tw-border tw-border-gray-300 tw-flex tw-flex-col lg:tw-flex-row tw-items-center lg:tw-items-start tw-gap-3">
          <img
            className="tw-w-full tw-h-auto tw-relative  lg:tw-h-[80px] lg:tw-w-28 tw-object-cover tw-rounded-lg"
            src={targetAssistantData?.images?.main}
            width="80px"
            alt={targetAssistantData?.code}
          />
          <div className="tw-flex tw-flex-col tw-justify-center tw-gap-4 tw-grow tw-shrink tw-basis-0">
            <div className="tw-flex tw-flex-col tw-gap-1">
              <div className="tw-text-gray-900 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">{targetAssistantData?.name}</div>
              <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{targetAssistantData?.description}</div>
            </div>
            <div className="tw-flex tw-flex-wrap lg:tw-flex-row tw-gap-2">
              {targetAssistantData?.skills?.map((skill, index) => (
                <div key={index} className="tw-inline-block tw-px-2.5 tw-py-0.5 tw-bg-blue-50 tw-rounded-lg">
                  <div className="tw-text-center tw-text-blue-800 tw-font-normal tw-font-['Lexend'] tw-leading-none tw-capitalize tw-pb-0.5 tw-pt-px !tw-text-xs">
                    {skill}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-px-1 tw-flex-col tw-justify-start tw-tw-items-start tw-inline-flex  tw-w-full">
      {content}

      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex tw-mb-8">
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
          <div className="tw-self-stretch tw-text-gray-900 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
            {t('Wizard/AiAdditionalInfo/key1')}
          </div>
          <div className="tw-self-stretch tw-text-gray-900 text-sm font-normal tw-font-['Lexend'] tw-leading-tight">
            {t('Wizard/AiAdditionalInfo/key2')}
            {wizard.category === 'widget' && t('Wizard/AiAdditionalInfo/key3')}
          </div>
        </div>
      </div>

      <Form.Group className="tw-mb-3">
        <Form.Label>{t('Wizard/AiAdditionalInfo/key5')}</Form.Label>
        <Form.Control
          as={'textarea'}
          maxLength={500}
          rows={2}
          placeholder={t('Wizard/AiAdditionalInfo/key6').toString()}
          defaultValue={(wizard.bot_config as AIKittyType).welcoming ?? ''}
          {...formBuilder.register('welcoming', {
            onChange: updateBasicInfos,
          })}
        />
        <HintBlock texts={[t('Wizard/AiAdditionalInfo/key8')]} />
      </Form.Group>

      <Form.Group className="tw-mb-3">
        <Form.Label>{t('Wizard/AiAdditionalInfo/key4')}</Form.Label>
        <div className="tw-mb-4">
          <BannerHelper message={t('HelpBanner/AdditionalInfo/hint')} />
        </div>
        <Form.Control
          placeholder={t('KnowledgeBasesCreateAssistantBot/ph-01')}
          type="text"
          as={'textarea'}
          maxLength={10000}
          rows={12}
          defaultValue={(wizard.bot_config as AIKittyType).additional_instructions.join() ?? ''}
          {...formBuilder.register('additional_instructions', {
            onChange: updateBasicInfos,
          })}
        />
      </Form.Group>

      {targetAssistantData?.required_instructions.map(ri => (
        <Form.Group key={ri.name} className="tw-mb-3">
          <Form.Label>{capitalizeAndRemoveUnderscore(labelMapper[ri.name])} *</Form.Label>
          <Form.Control
            as={ri.name === 'personality' ? 'textarea' : undefined}
            rows={ri.name === 'personality' ? 5 : undefined}
            type="text"
            placeholder={ri.description}
            isInvalid={!!formBuilder.formState.errors[ri.name]}
            defaultValue={(wizard.bot_config as AIKittyType).instructions[ri.name as KeyIndexed] ?? ''}
            {...formBuilder.register(`${ri.name}`, {
              onChange: updateExtraInfos,
            })}
          />
          <Form.Control.Feedback type="invalid">{getErrorMessage(formBuilder.formState.errors, ri.name) as string}</Form.Control.Feedback>
        </Form.Group>
      ))}
    </div>
  );
};

export default WrappedErrorBoundary(AiAdditionalInfoEdit);
